import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { ArrowLeftIcon } from 'lucide-react';
export const Verification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [isResending, setIsResending] = useState(false);
  // Get phone number from location state
  const phoneNumber = location.state?.phoneNumber || '+1 (XXX) XXX-XXXX';
  // Input references for auto-focus
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);
  const handleInputChange = (index: number, value: string) => {
    // Only allow one digit per input
    if (value.length > 1) {
      value = value.slice(0, 1);
    }
    // Only allow digits
    if (value && !/^\d+$/.test(value)) {
      return;
    }
    const newVerificationCode = [...verificationCode];
    newVerificationCode[index] = value;
    setVerificationCode(newVerificationCode);
    // Auto-focus to next input if current input is filled
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleResendCode = () => {
    // Simulate resending code
    setIsResending(true);
    setTimeout(() => {
      setIsResending(false);
      setTimer(60);
      // In a real app, we would call an API to resend the code
    }, 1500);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Check if code is complete
    if (verificationCode.join('').length !== 6) {
      return;
    }
    // In a real app, we would validate the code with an API
    // For now, just navigate to the profile page
    navigate('/profile');
  };
  const handleGoBack = () => {
    navigate(-1);
  };
  return <div className="min-h-screen flex items-center justify-center bg-background p-4" data-id="element-408">
      <div className="w-full max-w-md" data-id="element-409">
        <button onClick={handleGoBack} className="absolute left-4 top-4 p-2 text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors" data-id="element-410">
          <ArrowLeftIcon size={18} data-id="element-411" />
          <span data-id="element-412">Back</span>
        </button>
        <div className="text-center mb-8" data-id="element-413">
          <h1 className="text-3xl font-bold text-primary" data-id="element-414">Verification</h1>
          <p className="text-gray-600 mt-2" data-id="element-415">
            Enter the code sent to your phone
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100" data-id="element-416">
          <div className="text-center mb-6" data-id="element-417">
            <p className="text-sm text-gray-600" data-id="element-418">
              We've sent a 6-digit verification code to
            </p>
            <p className="font-medium text-gray-800 mt-1" data-id="element-419">{phoneNumber}</p>
          </div>
          <form onSubmit={handleSubmit} data-id="element-420">
            <div className="flex justify-center gap-2 mb-8" data-id="element-421">
              {verificationCode.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" value={digit} onChange={e => handleInputChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors" maxLength={1} autoFocus={index === 0} data-id="element-422" />)}
            </div>
            <Button type="submit" variant="primary" fullWidth size="lg" disabled={verificationCode.join('').length !== 6} data-id="element-423">
              Verify
            </Button>
          </form>
          <div className="mt-8 text-center" data-id="element-424">
            <p className="text-sm text-gray-600 mb-2" data-id="element-425">Didn't receive a code?</p>
            {timer > 0 ? <p className="text-sm text-gray-500" data-id="element-426">
                Resend code in <span className="font-medium" data-id="element-427">{timer}s</span>
              </p> : <button onClick={handleResendCode} disabled={isResending} className="text-primary hover:underline font-medium text-sm transition-colors" data-id="element-428">
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>}
          </div>
        </div>
      </div>
    </div>;
};